import React, { useCallback } from 'react';

import { useAppState, useSafeFocusEffect } from '@sg/garnish';

import { Bag } from '@order/features/ordering';
import { getStripeKeyPrefix, StripeElementsProvider } from '@order/integrations/stripe';
import { useFeatureFlag } from '@order/LaunchDarkly';

import { useIsLoggedIn } from '../../context/AuthMachine';
import { useCloseBag } from '../../hooks';
import { BagAnimatedDialog, BagContent } from './components';
import { useBagGestures, useOrderState } from './hooks';
import { getOrderPlaybackConfig } from './utils';

export const BagScreen = () => {
  const orderPlaybackConfigFlag = useFeatureFlag('CELS-4127-order-playback-config'); // prettier-ignore

  const { orderSubmissionDelay } = getOrderPlaybackConfig(orderPlaybackConfigFlag);

  const orderState = useOrderState({ orderSubmissionDelay });

  const {
    cart,
    isFetchingInitialData,
    isSubmittingOrder,
    isShowingModal,
    refetchWantedTimes,
    startOrdering,
    purchaseAttempt,
    stripeSession,
    total,
  } = orderState;

  const isLoggedIn = useIsLoggedIn();
  const isBagReady = !isFetchingInitialData && cart?.orderId;
  const shouldRenderAnimatedDialog = Boolean(isBagReady && isLoggedIn);

  // ──────────────────────────────────────────────────────────────────────────

  const closeBag = useCloseBag();

  // Prevent closing bag while submitting an order.
  const handleCloseBag = useCallback(() => {
    if (isSubmittingOrder) return;

    closeBag();
  }, [isSubmittingOrder, closeBag]);

  // Enable/disable gestures when modal is shown.
  useBagGestures(isShowingModal);

  // Fetch ordering data on bag focus.
  useSafeFocusEffect(startOrdering);

  // Refetch wanted times on app focus.
  useAppState({ onVisible: refetchWantedTimes });

  // ──────────────────────────────────────────────────────────────────────────

  return (
    <Bag.DynamicModal onClose={handleCloseBag}>
      <BagContent orderState={orderState} />

      {shouldRenderAnimatedDialog ? (
        <StripeElementsProvider amount={total} sessionId={stripeSession}>
          <BagAnimatedDialog key={getStripeKeyPrefix(purchaseAttempt)} orderState={orderState} />
        </StripeElementsProvider>
      ) : null}
    </Bag.DynamicModal>
  );
};
